import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';


const STANDARD_SHIPPING_VARIANT_ID = 54382995800407;
const EXPRESS_SHIPPING_VARIANT_ID = 54877919445335;


/**
 * Ermittelt nur die für den Kunden echten Warenkorbprodukte.
 * Standard- und Express-Versandpauschale werden nicht mitgezählt.
 *
 * @param {object | undefined} cart
 * @returns {number | null}
 */
function getVisibleCartCount(cart) {
  if (!cart?.items || !Array.isArray(cart.items)) {
    return null;
  }

  return cart.items.reduce((count, item) => {
    const variantId = Number(item.variant_id);

    if (
      variantId === STANDARD_SHIPPING_VARIANT_ID ||
      variantId === EXPRESS_SHIPPING_VARIANT_ID
    ) {
      return count;
    }

    return count + Number(item.quantity || 0);
  }, 0);
}


/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = [
    'cartBubble',
    'cartBubbleText',
    'cartBubbleCount'
  ];


  /** @type {number} */
  get currentCartCount() {
    return parseInt(
      this.refs.cartBubbleCount.textContent ?? '0',
      10
    ) || 0;
  }


  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent =
      value < 100 ? String(value) : '';
  }


  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.onCartUpdate
    );

    window.addEventListener(
      'pageshow',
      this.onPageShow
    );

    this.ensureCartBubbleIsCorrect();
  }


  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.onCartUpdate
    );

    window.removeEventListener(
      'pageshow',
      this.onPageShow
    );
  }


  /**
   * Handles the page show event when the page is restored from cache.
   *
   * @param {PageTransitionEvent} event
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };


  /**
   * Handles cart update events.
   *
   * @param {CartUpdateEvent} event
   */
  onCartUpdate = async (event) => {

    /*
     * Wenn der vollständige Warenkorb im Event vorhanden ist,
     * verwenden wir immer unseren eigenen sichtbaren Count.
     */
    const visibleCartCount =
      getVisibleCartCount(
        event.detail.resource
      );


    if (visibleCartCount !== null) {

      this.renderCartBubble(
        visibleCartCount,
        false
      );

      return;
    }


    /*
     * Fallback für Theme-Events ohne vollständige Cart-Daten
     */
    const itemCount =
      event.detail.data?.itemCount ?? 0;

    const comingFromProductForm =
      event.detail.data?.source ===
      'product-form-component';


    this.renderCartBubble(
      itemCount,
      comingFromProductForm
    );
  };


  /**
   * Renders the cart bubble.
   *
   * @param {number} itemCount
   * @param {boolean} comingFromProductForm
   * @param {boolean} animate
   */
  renderCartBubble = async (
    itemCount,
    comingFromProductForm,
    animate = true
  ) => {

    const nextCount =
      comingFromProductForm
        ? this.currentCartCount + itemCount
        : itemCount;


    this.refs.cartBubbleCount.classList.toggle(
      'hidden',
      nextCount === 0
    );


    this.refs.cartBubble.classList.toggle(
      'visually-hidden',
      nextCount === 0
    );


    this.currentCartCount =
      nextCount;


    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      nextCount > 0
    );


    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(nextCount),
        timestamp: Date.now()
      })
    );


    if (
      !animate ||
      nextCount === 0
    ) {
      return;
    }


    await new Promise(
      (resolve) =>
        requestAnimationFrame(resolve)
    );


    this.refs.cartBubble.classList.add(
      'cart-bubble--animating'
    );


    await onAnimationEnd(
      this.refs.cartBubbleText
    );


    this.refs.cartBubble.classList.remove(
      'cart-bubble--animating'
    );
  };


  /**
   * Prüft den echten Shopify-Warenkorb beim Laden der Seite.
   *
   * Dadurch kann ein alter falscher Wert wie "3" aus
   * sessionStorage den korrekten Liquid-Wert nicht wieder
   * überschreiben.
   */
  ensureCartBubbleIsCorrect = async () => {

    if (!this.refs.cartBubbleCount) {
      return;
    }


    try {

      const response =
        await fetch(
          '/cart.js',
          {
            credentials: 'same-origin',
            cache: 'no-store'
          }
        );


      if (!response.ok) {
        return;
      }


      const cart =
        await response.json();


      const visibleCartCount =
        getVisibleCartCount(cart);


      if (visibleCartCount === null) {
        return;
      }


      if (
        visibleCartCount !==
        this.currentCartCount
      ) {

        await this.renderCartBubble(
          visibleCartCount,
          false,
          false
        );

      } else {

        /*
         * Auch wenn die sichtbare Zahl schon stimmt,
         * SessionStorage mit dem korrekten Wert überschreiben.
         */
        sessionStorage.setItem(
          'cart-count',
          JSON.stringify({
            value: String(visibleCartCount),
            timestamp: Date.now()
          })
        );

      }


    } catch (error) {

      console.warn(
        'DTF: Warenkorbanzahl konnte nicht geprüft werden.',
        error
      );

    }

  };

}


if (!customElements.get('cart-icon')) {
  customElements.define(
    'cart-icon',
    CartIcon
  );
}